// ─── utils/profileCompleteness.js ────────────────────────────────────────────
// Client-side mirrors of the server's two completeness rules, so a gate can be
// shown before a request is made instead of only after it comes back 403.
//
// The server is still the enforcement — utils/sellerProfile.js and
// utils/customerProfile.js are the source of truth, and both are Node-only
// modules that can't be imported into the bundle. Anything changed there has
// to be changed here too; the checklists below exist so that when it is, there
// is one place on this side to change rather than four inline conditionals.

function bioHasText(bio) {
  return String(bio || '').replace(/<[^>]*>/g, '').trim().length > 0;
}

// education/experience are arrays of { role, org, dates, description } (see
// UserModel.js) — an entry only counts once one of its fields actually has
// text, not just because the array is non-empty (an added-then-cleared row
// stays in the array as all-blank strings).
function workHistoryHasContent(entries) {
  return Array.isArray(entries) && entries.some((e) =>
    [e?.role, e?.org, e?.dates, e?.description].some((f) => String(f || '').trim().length > 0),
  );
}

// ── Freelancer side — gates publishing a listing and applying to a job ───────
// Mirrors utils/sellerProfile.js, including the sellerOnboardingComplete fast
// path that grandfathers in anyone who finished the wizard.
// `sellerOnboardingComplete` marks anyone who finished the wizard, and the
// server treats that flag alone as complete — it grandfathers in sellers who
// built a profile before these particular fields existed. That fast path has
// to live INSIDE the row checks, not just in the boolean below: the checklist
// is what tells a user whether they're blocked, and without it a
// grandfathered seller is shown a list of "required" rows and told they can't
// publish or apply, while the server lets them do both. Server-side twin:
// utils/sellerProfile.js.
export function sellerProfileChecklist(user) {
  const p = user?.freelancerProfile || {};
  const grandfathered = Boolean(p.sellerOnboardingComplete);
  return [
    { key: 'headline', label: 'Add a professional headline', done: grandfathered || Boolean(p.headline) },
    { key: 'bio',      label: 'Write a short bio',           done: grandfathered || bioHasText(p.bio) },
    { key: 'skills',   label: 'Pick at least one skill',     done: grandfathered || (Array.isArray(p.skills) && p.skills.length > 0) },
  ];
}

export function isSellerProfileComplete(user) {
  return sellerProfileChecklist(user).every((item) => item.done);
}

// ── Client side — gates posting a job ────────────────────────────────────────
// Mirrors utils/customerProfile.js. A logo is deliberately not on this list:
// it makes the client card look far better and is prompted for in the wizard,
// but a student hiring a tutor rarely has one, and it isn't worth blocking a
// job posting over.
export function businessProfileChecklist(user) {
  const p = user?.customerProfile || {};
  return [
    { key: 'companyName', label: 'Name',        done: Boolean(p.companyName) },
    { key: 'industry',    label: 'Industry',    done: Boolean(p.industry) },
    { key: 'about',       label: 'Description', done: Boolean(p.about) },
    { key: 'city',        label: 'Location',    done: Boolean(p.location?.city) },
  ];
}

export function isBusinessProfileComplete(user) {
  return businessProfileChecklist(user).every((item) => item.done);
}

// ── Which mode is this account in right now ──────────────────────────────────
// `defaultView` is written optimistically by the nav's mode switcher
// (App.jsx switchToMode) before the PATCH lands, so reading it here IS reading
// the live mode — flip the switcher and every checklist below follows.
//
// Anything that isn't explicitly 'hiring' resolves to freelancing, so an
// account that has never picked a side gets the freelancer list rather than
// being asked about a company it may not have.
export function resolveMode(user) {
  return user?.defaultView === 'hiring' ? 'hiring' : 'freelancing';
}

// ── The profile checklists ───────────────────────────────────────────────────
// One definition, rendered in two homes (the profile page and the hiring
// dashboard) so nobody ever sees two disagreeing "3/6 done" counts.
//
// `required: true` means this row gates something real — it's the reason a
// button elsewhere won't work, which is why those rows can't be dismissed.
// `section` is the Profile tab that fixes it, so a row jumps straight there.
//
// Payment is required on both sides and is not the same account on each:
// freelancers connect a Stripe payout account (money out), clients save a card
// (money in). One row each, never both — see utils/stripeBuyer.js.

// Built on sellerProfileChecklist rather than re-deriving the same three
// fields, so the rows here and the rows the gate blocks on can't drift apart.
function freelancingChecklist(user) {
  const p = user?.freelancerProfile || {};
  return [
    ...sellerProfileChecklist(user).map((item) => ({
      ...item, section: 'overview', required: true,
    })),
    { key: 'payouts',    label: 'Set up payouts',      section: 'payouts',  required: true,  done: Boolean(p.stripeOnboardingComplete) },
    // The only way a school gets onto a profile (no typed-in school any more).
    // Mirrors UNIVERSITY_VERIFICATION_ENFORCED (server/config/featureFlags.js),
    // now back on — this genuinely blocks publishing, so "Required" is honest.
    { key: 'universityVerified', label: 'Verify your university', section: 'university-verification', required: true, done: user?.universityVerification?.status === 'verified' },
    { key: 'education',  label: 'Add your education',  section: 'overview', required: false, done: workHistoryHasContent(p.education) },
    { key: 'experience', label: 'Add your experience', section: 'overview', required: false, done: workHistoryHasContent(p.experience) },
  ];
}

function hiringChecklist(user) {
  const p = user?.customerProfile || {};
  return [
    ...businessProfileChecklist(user).map((item) => ({
      ...item, section: 'business-profile', required: true,
    })),
    { key: 'paymentMethod', label: 'Save a card',    section: 'payment-methods', required: true,  done: Boolean(p.hasPaymentMethod) },
    { key: 'logo',          label: 'Upload a logo',  section: 'business-profile', required: false, done: Boolean(p.logoUrl) },
    { key: 'website',       label: 'Add your website', section: 'business-profile', required: false, done: Boolean(p.website) },
  ];
}

// The blocked line describes the list, not the account. Two of these rows —
// payouts and the saved card — are read from cached Stripe flags this side
// can't verify, and `hasPaymentMethod` in particular has no webhook keeping it
// current. Phrasing it as "still needed" is true even when a flag lags; "you
// can't post a job" would be a flat falsehood to someone who just added a card
// in the Stripe dashboard. The real gates live-check before turning anyone
// away (utils/stripeBuyer.js, utils/stripeSeller.js).
const CHECKLIST_COPY = {
  freelancing: {
    title: 'Complete your profile',
    subtitle: 'Finish these so clients can find and hire you.',
    blocked: 'Still needed before you can open a service or apply to jobs',
  },
  hiring: {
    title: 'Complete your business profile',
    subtitle: 'Freelancers read this before deciding whether to write you a proposal.',
    blocked: 'Still needed before you can post a job',
  },
};

export function checklistFor(user) {
  const mode = resolveMode(user);
  const items = mode === 'hiring' ? hiringChecklist(user) : freelancingChecklist(user);
  const blocking = items.filter((i) => i.required && !i.done);
  return {
    mode,
    items,
    ...CHECKLIST_COPY[mode],
    done: items.filter((i) => i.done).length,
    total: items.length,
    blockingCount: blocking.length,
    canProceed: blocking.length === 0,
  };
}
